/* ============================================================
   Endlessprints mug designer (full page, ?view=designer).
   - Board = the flat wrap layout. Elements (photos/text) get
     Customify-style transform controls: drag to move, corner
     handle to scale, top handle to rotate, × to delete.
   - Everything re-renders onto a 2250×1050 canvas — the exact
     size the wrap is printed at — which also textures the mug.
   - Seam + no-print gap live at the handle (5% each end).
   - Editing: layers, duplicate, flip, opacity, photo filters,
     shapes (square/rounded/circle), white border, drop shadow,
     fonts, text colour (incl. custom), outline, text shadow,
     wrap background colour.
   ============================================================ */
(function () {
  "use strict";
  var board = document.querySelector("[data-board]");
  if (!board) return;

  var MARGIN = 0.05; /* no-print gap fraction at each end (by the handle) */
  var els = [];
  var selected = null;
  var mug = null;

  /* Mug colour (handle + rim + inside). Picker only exists on
     products tagged customhandle; everything else keeps black. */
  var mugColorBar = document.querySelector("[data-mugcolorbar]");
  var mugColor = mugColorBar ? "#ffffff" : "#1c1c22";
  var mugColorName = "White";

  var FILTERS = {
    none: "",
    bw: "grayscale(1)",
    sepia: "sepia(.85)",
    warm: "saturate(1.25) sepia(.18) brightness(1.03)",
    cool: "saturate(1.1) hue-rotate(-12deg) brightness(1.02)",
    pop: "saturate(1.5) contrast(1.12)"
  };

  /* Print file: 2250×1050 px — the real print dimensions */
  var tex = document.createElement("canvas");
  tex.width = 2250; tex.height = 1050;
  var tctx = tex.getContext("2d");

  /* WebGL wants power-of-two textures — mirror the print canvas
     onto a 2048×1024 copy that feeds the 3D mug */
  var glCanvas = document.createElement("canvas");
  glCanvas.width = 2048; glCanvas.height = 1024;
  var gctx = glCanvas.getContext("2d");

  function shapePath(ctx, w, h, shape) {
    ctx.beginPath();
    if (shape === "circle") {
      ctx.arc(0, 0, w / 2, 0, Math.PI * 2);
    } else if (shape === "rounded") {
      var r = Math.min(w, h) * 0.12;
      ctx.moveTo(-w / 2 + r, -h / 2);
      ctx.arcTo(w / 2, -h / 2, w / 2, h / 2, r);
      ctx.arcTo(w / 2, h / 2, -w / 2, h / 2, r);
      ctx.arcTo(-w / 2, h / 2, -w / 2, -h / 2, r);
      ctx.arcTo(-w / 2, -h / 2, w / 2, -h / 2, r);
      ctx.closePath();
    } else {
      ctx.rect(-w / 2, -h / 2, w, h);
    }
  }

  /* ---------------- model → print canvas ---------------- */
  function renderTexture() {
    tctx.fillStyle = "#ffffff";
    tctx.fillRect(0, 0, tex.width, tex.height);
    els.forEach(function (el) {
      var cx = el.cx * tex.width, cy = el.cy * tex.height;
      tctx.save();
      tctx.translate(cx, cy);
      tctx.rotate(el.rot || 0);
      tctx.globalAlpha = el.opacity != null ? el.opacity : 1;
      if (el.type === "img" && el.img && el.img.complete) {
        var w = el.w * tex.width;
        var h = el.shape === "circle" ? w : w * (el.img.naturalHeight / el.img.naturalWidth);
        shapePath(tctx, w, h, el.shape);
        if (el.shadow) {
          tctx.save();
          tctx.shadowColor = "rgba(20,15,35,.4)";
          tctx.shadowBlur = w * 0.06;
          tctx.shadowOffsetY = w * 0.025;
          tctx.fillStyle = "#ffffff";
          tctx.fill();
          tctx.restore();
        }
        tctx.save();
        tctx.clip();
        if (el.filter && FILTERS[el.filter]) tctx.filter = FILTERS[el.filter];
        if (el.flip) tctx.scale(-1, 1);
        if (el.shape === "circle") {
          /* centre-crop to a square so the circle crop matches the board */
          var s = Math.min(el.img.naturalWidth, el.img.naturalHeight);
          tctx.drawImage(el.img,
            (el.img.naturalWidth - s) / 2, (el.img.naturalHeight - s) / 2, s, s,
            -w / 2, -w / 2, w, w);
        } else {
          tctx.drawImage(el.img, -w / 2, -h / 2, w, h);
        }
        tctx.restore();
        if (el.border) {
          shapePath(tctx, w, h, el.shape);
          tctx.lineWidth = Math.min(w, h) * 0.045;
          tctx.strokeStyle = "#ffffff";
          tctx.stroke();
        }
      } else if (el.type === "text" && el.text) {
        var fs = el.size * tex.height;
        tctx.font = "700 " + fs + 'px "' + (el.font || "Quicksand") + '", sans-serif';
        tctx.textAlign = "center";
        tctx.textBaseline = "middle";
        if (el.shadow) {
          tctx.shadowColor = "rgba(20,15,35,.45)";
          tctx.shadowBlur = fs * 0.14;
          tctx.shadowOffsetY = fs * 0.05;
        }
        if (el.outline !== false) {
          tctx.lineWidth = fs * 0.18;
          tctx.strokeStyle = el.color === "#ffffff" ? "rgba(43,36,64,.85)" : "rgba(255,255,255,.9)";
          tctx.strokeText(el.text, 0, 0);
        }
        tctx.fillStyle = el.color;
        tctx.fillText(el.text, 0, 0);
      }
      tctx.restore();
    });
    /* keep the handle gap clean (never printed) */
    tctx.fillStyle = "#ffffff";
    tctx.fillRect(0, 0, tex.width * MARGIN, tex.height);
    tctx.fillRect(tex.width * (1 - MARGIN), 0, tex.width * MARGIN, tex.height);
    gctx.drawImage(tex, 0, 0, glCanvas.width, glCanvas.height);
    if (mug) mug.refresh();
  }

  /* ---------------- board DOM ---------------- */
  function boardRect() { return board.getBoundingClientRect(); }

  function zSync() {
    els.forEach(function (e, i) { e.node.style.zIndex = 2 + i; });
  }

  function paintEl(el) {
    var r = boardRect();
    el.node.style.left = el.cx * 100 + "%";
    el.node.style.top = el.cy * 100 + "%";
    el.node.style.transform = "translate(-50%,-50%) rotate(" + (el.rot || 0) + "rad)";
    if (el.type === "img") {
      el.node.style.width = el.w * 100 + "%";
      var im = el.node.querySelector(".dz-el__img");
      im.style.filter = (FILTERS[el.filter] || "") +
        (el.shadow ? " drop-shadow(0 4px 9px rgba(20,15,35,.4))" : "");
      im.style.opacity = el.opacity != null ? el.opacity : 1;
      im.style.transform = el.flip ? "scaleX(-1)" : "";
      if (el.shape === "circle") {
        im.style.borderRadius = "50%";
        im.style.aspectRatio = "1 / 1";
        im.style.objectFit = "cover";
      } else {
        im.style.borderRadius = el.shape === "rounded" ? "10%" : "0";
        im.style.aspectRatio = "";
        im.style.objectFit = "";
      }
      im.style.border = el.border ? "3px solid #fff" : "0";
    } else {
      el.node.style.width = "auto";
      var span = el.node.querySelector(".dz-el__text");
      span.textContent = el.text || "Your text";
      span.style.fontSize = el.size * r.height + "px";
      span.style.fontFamily = '"' + (el.font || "Quicksand") + '", sans-serif';
      span.style.color = el.color;
      span.style.opacity = el.opacity != null ? el.opacity : 1;
      var halo = el.outline !== false
        ? (el.color === "#ffffff"
          ? "0 0 3px rgba(43,36,64,.8), 0 0 6px rgba(43,36,64,.5)"
          : "0 0 3px #fff, 0 0 6px #fff")
        : "";
      var drop = el.shadow ? "0 4px 8px rgba(20,15,35,.45)" : "";
      span.style.textShadow = [halo, drop].filter(Boolean).join(", ");
    }
    updateEmpty();
    renderTexture();
  }

  function updateEmpty() {
    var empty = document.querySelector("[data-board-empty]");
    if (empty) empty.hidden = els.length > 0;
  }

  /* ---------------- properties panel ---------------- */
  var props = document.querySelector("[data-props]");
  var propsImg = document.querySelector("[data-props-img]");
  var propsText = document.querySelector("[data-props-text]");

  function setChipActive(sel, attr, value) {
    props.querySelectorAll(sel).forEach(function (c) {
      c.classList.toggle("is-active", c.getAttribute(attr) === value);
    });
  }

  function syncProps(el) {
    if (!props) return;
    props.hidden = !el;
    if (!el) return;
    propsImg.hidden = el.type !== "img";
    propsText.hidden = el.type !== "text";
    var flip = props.querySelector("[data-p-flip]");
    if (flip) {
      flip.hidden = el.type !== "img";
      flip.classList.toggle("is-active", !!el.flip);
    }
    props.querySelector("[data-p-opacity]").value = Math.round((el.opacity != null ? el.opacity : 1) * 100);
    if (el.type === "img") {
      setChipActive("[data-p-filter]", "data-p-filter", el.filter || "none");
      setChipActive("[data-p-shape]", "data-p-shape", el.shape || "rect");
      props.querySelector("[data-p-border]").classList.toggle("is-active", !!el.border);
      props.querySelector("[data-p-shadow]").classList.toggle("is-active", !!el.shadow);
    } else {
      props.querySelector("[data-tb-text]").value = el.text;
      var fontSel = props.querySelector("[data-tb-font]");
      if (fontSel) fontSel.value = el.font || "Quicksand";
      props.querySelector("[data-tb-size]").value = Math.round(el.size * 100);
      props.querySelectorAll("[data-tb-color]").forEach(function (s) {
        s.classList.toggle("is-active", s.getAttribute("data-tb-color") === el.color);
      });
      var custom = props.querySelector("[data-tb-custom]");
      if (custom && /^#[0-9a-f]{6}$/i.test(el.color)) custom.value = el.color;
      props.querySelector("[data-tb-outline]").classList.toggle("is-active", el.outline !== false);
      props.querySelector("[data-tb-shadow]").classList.toggle("is-active", !!el.shadow);
    }
  }

  function select(el) {
    selected = el;
    els.forEach(function (e) { e.node.classList.toggle("is-selected", e === el); });
    syncProps(el);
  }

  function removeEl(el) {
    els = els.filter(function (e) { return e !== el; });
    el.node.remove();
    if (selected === el) select(null);
    zSync();
    updateEmpty();
    renderTexture();
  }

  function makeNode(el) {
    var n = document.createElement("div");
    n.className = "dz-el";
    n.innerHTML =
      (el.type === "img" ? '<img class="dz-el__img" alt="" draggable="false">' : '<span class="dz-el__text"></span>') +
      '<button type="button" class="dz-del" aria-label="Remove">✕</button>' +
      '<span class="dz-rot" aria-hidden="true">⟳</span>' +
      ["nw", "ne", "sw", "se"].map(function (c) { return '<span class="dz-h dz-h--' + c + '"></span>'; }).join("");
    el.node = n;
    board.appendChild(n);
    zSync();
    if (el.type === "img") n.querySelector(".dz-el__img").src = el.img.src;

    n.addEventListener("pointerdown", function (e) {
      e.preventDefault();
      select(el);
      var r = boardRect();
      var mode = e.target.classList.contains("dz-h") ? "scale"
        : e.target.classList.contains("dz-rot") ? "rotate"
        : e.target.classList.contains("dz-del") ? "del" : "move";
      if (mode === "del") { removeEl(el); return; }
      var cx0 = r.left + el.cx * r.width, cy0 = r.top + el.cy * r.height;
      var start = {
        x: e.clientX, y: e.clientY, cx: el.cx, cy: el.cy, w: el.w, size: el.size, rot: el.rot || 0,
        d0: Math.hypot(e.clientX - cx0, e.clientY - cy0) || 1,
        a0: Math.atan2(e.clientY - cy0, e.clientX - cx0)
      };
      n.setPointerCapture(e.pointerId);
      function move(ev) {
        if (mode === "move") {
          el.cx = Math.min(1, Math.max(0, start.cx + (ev.clientX - start.x) / r.width));
          el.cy = Math.min(1, Math.max(0, start.cy + (ev.clientY - start.y) / r.height));
        } else if (mode === "scale") {
          var k = (Math.hypot(ev.clientX - cx0, ev.clientY - cy0) || 1) / start.d0;
          if (el.type === "img") el.w = Math.min(1.4, Math.max(0.04, start.w * k));
          else el.size = Math.min(0.4, Math.max(0.03, start.size * k));
        } else if (mode === "rotate") {
          el.rot = start.rot + (Math.atan2(ev.clientY - cy0, ev.clientX - cx0) - start.a0);
        }
        paintEl(el);
      }
      function up() {
        n.removeEventListener("pointermove", move);
        n.removeEventListener("pointerup", up);
        n.removeEventListener("pointercancel", up);
      }
      n.addEventListener("pointermove", move);
      n.addEventListener("pointerup", up);
      n.addEventListener("pointercancel", up);
    });
  }

  function addImage(file) {
    if (!file || file.type.indexOf("image/") !== 0) return;
    var img = new Image();
    img.onload = function () {
      var el = {
        type: "img", file: file, img: img, cx: 0.5, cy: 0.46, w: 0.28, rot: 0,
        opacity: 1, filter: "none", shape: "rect", flip: false, border: false, shadow: false
      };
      els.push(el);
      makeNode(el);
      paintEl(el);
      select(el);
    };
    img.src = URL.createObjectURL(file);
  }

  function addText() {
    var el = {
      type: "text", text: "Your text", color: "#2b2440", font: "Quicksand",
      cx: 0.5, cy: 0.75, size: 0.12, w: 0, rot: 0,
      opacity: 1, outline: true, shadow: false
    };
    els.push(el);
    makeNode(el);
    paintEl(el);
    select(el);
    var tb = document.querySelector("[data-tb-text]");
    if (tb) { tb.focus(); tb.select(); }
  }

  function duplicateEl(src) {
    var el = {};
    for (var k in src) if (k !== "node") el[k] = src[k];
    el.cx = Math.min(0.95, src.cx + 0.05);
    el.cy = Math.min(0.95, src.cy + 0.07);
    els.push(el);
    makeNode(el);
    paintEl(el);
    select(el);
  }

  /* toolbar */
  var fileInput = document.querySelector("[data-dz-file]");
  document.querySelector("[data-dz-add-photo]").addEventListener("click", function () { fileInput.click(); });
  fileInput.addEventListener("change", function () {
    Array.prototype.forEach.call(fileInput.files, addImage);
    fileInput.value = "";
  });
  document.querySelector("[data-dz-add-text]").addEventListener("click", addText);
  board.addEventListener("pointerdown", function (e) { if (e.target === board) select(null); });

  /* mug colour picker (customhandle products) */
  if (mugColorBar) {
    mugColorBar.querySelectorAll("[data-mug-color]").forEach(function (s) {
      s.addEventListener("click", function () {
        mugColor = s.getAttribute("data-mug-color");
        mugColorName = s.getAttribute("data-mug-name") || mugColor;
        mugColorBar.querySelectorAll("[data-mug-color]").forEach(function (x) {
          x.classList.toggle("is-active", x === s);
        });
        if (mug) mug.setColor(mugColor);
      });
    });
  }

  /* properties panel wiring */
  if (props) {
    props.querySelector("[data-p-back]").addEventListener("click", function () {
      if (!selected) return;
      var i = els.indexOf(selected);
      if (i > 0) { els.splice(i, 1); els.splice(i - 1, 0, selected); zSync(); renderTexture(); }
    });
    props.querySelector("[data-p-fwd]").addEventListener("click", function () {
      if (!selected) return;
      var i = els.indexOf(selected);
      if (i > -1 && i < els.length - 1) { els.splice(i, 1); els.splice(i + 1, 0, selected); zSync(); renderTexture(); }
    });
    props.querySelector("[data-p-dup]").addEventListener("click", function () {
      if (selected) duplicateEl(selected);
    });
    props.querySelector("[data-p-flip]").addEventListener("click", function () {
      if (selected && selected.type === "img") { selected.flip = !selected.flip; syncProps(selected); paintEl(selected); }
    });
    props.querySelector("[data-p-opacity]").addEventListener("input", function (e) {
      if (selected) { selected.opacity = e.target.value / 100; paintEl(selected); }
    });

    /* photo */
    props.querySelectorAll("[data-p-filter]").forEach(function (c) {
      c.addEventListener("click", function () {
        if (selected && selected.type === "img") {
          selected.filter = c.getAttribute("data-p-filter");
          syncProps(selected); paintEl(selected);
        }
      });
    });
    props.querySelectorAll("[data-p-shape]").forEach(function (c) {
      c.addEventListener("click", function () {
        if (selected && selected.type === "img") {
          selected.shape = c.getAttribute("data-p-shape");
          syncProps(selected); paintEl(selected);
        }
      });
    });
    props.querySelector("[data-p-border]").addEventListener("click", function () {
      if (selected && selected.type === "img") { selected.border = !selected.border; syncProps(selected); paintEl(selected); }
    });
    props.querySelector("[data-p-shadow]").addEventListener("click", function () {
      if (selected && selected.type === "img") { selected.shadow = !selected.shadow; syncProps(selected); paintEl(selected); }
    });

    /* text */
    props.querySelector("[data-tb-text]").addEventListener("input", function (e) {
      if (selected && selected.type === "text") { selected.text = e.target.value; paintEl(selected); }
    });
    props.querySelector("[data-tb-size]").addEventListener("input", function (e) {
      if (selected && selected.type === "text") { selected.size = e.target.value / 100; paintEl(selected); }
    });
    var fontSel = props.querySelector("[data-tb-font]");
    if (fontSel) {
      fontSel.addEventListener("change", function () {
        if (selected && selected.type === "text") {
          selected.font = fontSel.value;
          /* make sure the webfont is loaded before drawing it to canvas */
          if (document.fonts && document.fonts.load) {
            document.fonts.load('700 32px "' + fontSel.value + '"').then(function () { if (selected) paintEl(selected); });
          }
          paintEl(selected);
        }
      });
    }
    props.querySelectorAll("[data-tb-color]").forEach(function (s) {
      s.addEventListener("click", function () {
        if (selected && selected.type === "text") {
          selected.color = s.getAttribute("data-tb-color");
          syncProps(selected); paintEl(selected);
        }
      });
    });
    var custom = props.querySelector("[data-tb-custom]");
    if (custom) custom.addEventListener("input", function () {
      if (selected && selected.type === "text") { selected.color = custom.value; syncProps(selected); paintEl(selected); }
    });
    props.querySelector("[data-tb-outline]").addEventListener("click", function () {
      if (selected && selected.type === "text") { selected.outline = selected.outline === false; syncProps(selected); paintEl(selected); }
    });
    props.querySelector("[data-tb-shadow]").addEventListener("click", function () {
      if (selected && selected.type === "text") { selected.shadow = !selected.shadow; syncProps(selected); paintEl(selected); }
    });
  }

  /* ---------------- 3D mug ---------------- */
  function buildMug() {
    var mount = document.querySelector("[data-mug3d]");
    if (!mount || !window.THREE) return null;
    var T = window.THREE;
    var size = Math.min(mount.clientWidth || 340, 380);
    var renderer = new T.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(size, size);
    mount.appendChild(renderer.domElement);

    var scene = new T.Scene();
    var camera = new T.PerspectiveCamera(32, 1, 0.1, 100);
    camera.position.set(0, 1.1, 8.2);
    camera.lookAt(0, 0, 0);
    scene.add(new T.AmbientLight(0xffffff, 0.85));
    var d1 = new T.DirectionalLight(0xffffff, 0.65); d1.position.set(3, 5, 4); scene.add(d1);
    var d2 = new T.DirectionalLight(0xffffff, 0.25); d2.position.set(-4, 2, -3); scene.add(d2);

    var texture = new T.CanvasTexture(glCanvas);
    texture.anisotropy = 4;

    var g = new T.Group();
    /* accent = handle + rim + inside; recolourable on customhandle mugs */
    var black = new T.MeshPhongMaterial({ color: new T.Color(mugColor), shininess: 40 });
    var white = new T.MeshPhongMaterial({ color: 0xffffff, shininess: 30 });
    var accentMats = [black];

    /* thetaStart puts the texture seam at +X — exactly where the
       handle sits — so the seam and the no-print gap hide behind it */
    var wall = new T.Mesh(
      new T.CylinderGeometry(1.3, 1.3, 3, 96, 1, true, Math.PI / 2, Math.PI * 2),
      new T.MeshPhongMaterial({ map: texture, shininess: 26 })
    );
    g.add(wall);
    var inner = new T.Mesh(new T.CylinderGeometry(1.24, 1.24, 2.96, 96, 1, true), black.clone());
    inner.material.side = T.BackSide;
    accentMats.push(inner.material);
    g.add(inner);
    g.add(new T.Mesh(new T.TorusGeometry(1.27, 0.055, 14, 96), black).translateY(1.5).rotateX(Math.PI / 2));
    var ib = new T.Mesh(new T.CircleGeometry(1.24, 96), black);
    ib.rotation.x = -Math.PI / 2; ib.position.y = -1.44; g.add(ib);
    var bb = new T.Mesh(new T.CircleGeometry(1.3, 96), white);
    bb.rotation.x = Math.PI / 2; bb.position.y = -1.5; g.add(bb);
    var handle = new T.Mesh(new T.TorusGeometry(0.58, 0.15, 18, 60), black);
    handle.position.set(1.46, 0, 0);
    handle.scale.set(1, 1.25, 1);
    g.add(handle);

    /* Colour-changing (magic) mug: a black coating that sweeps
       up and down over the design, like heat revealing the print */
    var magicCover = null;
    if (mount.getAttribute("data-magic") === "1") {
      magicCover = new T.Mesh(
        new T.CylinderGeometry(1.315, 1.315, 3, 96, 1, true),
        new T.MeshPhongMaterial({ color: 0x151518, shininess: 45 })
      );
      g.add(magicCover);
    }

    g.rotation.y = 2.2; /* start facing the middle of the design */
    scene.add(g);

    var spin = null, touched = false;
    var el = renderer.domElement;
    el.style.touchAction = "none";
    el.addEventListener("pointerdown", function (e) { spin = { x: e.clientX, ry: g.rotation.y }; touched = true; el.setPointerCapture(e.pointerId); });
    el.addEventListener("pointermove", function (e) { if (spin) g.rotation.y = spin.ry + (e.clientX - spin.x) * 0.012; });
    el.addEventListener("pointerup", function () { spin = null; });
    el.addEventListener("pointercancel", function () { spin = null; });

    var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    (function loop() {
      if (!touched && !reduce) g.rotation.y += 0.004;
      if (magicCover) {
        /* 0 → fully black, 1 → fully revealed; eased sweep with a
           pause at each end. Coating stays anchored to the rim and
           recedes upwards as the "heat" rises, then comes back. */
        var t = (Math.sin(performance.now() * 0.0006) + 1) / 2;
        t = t * t * (3 - 2 * t);
        var k = Math.max(0.001, 1 - t);
        magicCover.scale.y = k;
        magicCover.position.y = 1.5 - 1.5 * k; /* keep top edge at the rim */
      }
      renderer.render(scene, camera);
      requestAnimationFrame(loop);
    })();

    return {
      refresh: function () { texture.needsUpdate = true; },
      setColor: function (hex) {
        accentMats.forEach(function (m) { m.color.set(hex); });
      },
      snapshot: function (done) {
        g.rotation.y = 2.2;
        if (magicCover) magicCover.visible = false;
        renderer.render(scene, camera);
        renderer.domElement.toBlob(function (b) {
          done(b ? new File([b], "design-preview.png", { type: "image/png" }) : null);
        }, "image/png", 0.92);
      }
    };
  }

  function initMug() {
    if (window.THREE) { mug = buildMug(); renderTexture(); }
    else setTimeout(initMug, 150); /* three.min.js is deferred */
  }
  initMug();
  renderTexture();

  /* ---------------- add to basket ---------------- */
  var form = document.querySelector("[data-designer-form]");
  if (form) {
    var busy = false;
    form.addEventListener("submit", function (e) {
      if (busy) return; /* second pass: let it post */
      e.preventDefault();
      busy = true;
      var btn = form.querySelector("[data-dz-save]");
      if (btn) btn.disabled = true;

      /* customer photos as individual order files */
      var n = 0;
      els.forEach(function (el) {
        if (el.type === "img" && el.file) {
          n++;
          var inp = document.createElement("input");
          inp.type = "file";
          inp.name = "properties[Your photo" + (n > 1 ? " " + n : "") + "]";
          inp.className = "visually-hidden";
          var dt = new DataTransfer();
          dt.items.add(el.file);
          inp.files = dt.files;
          form.appendChild(inp);
        }
      });
      /* chosen mug colour as a readable order property */
      if (mugColorBar) {
        var mc = document.createElement("input");
        mc.type = "hidden";
        mc.name = "properties[Mug colour]";
        mc.value = mugColorName;
        form.appendChild(mc);
      }
      var texts = els.filter(function (el) { return el.type === "text" && el.text; })
        .map(function (el) { return el.text; }).join(" | ");
      var tProp = form.querySelector("[data-dz-text-prop]");
      if (tProp && texts) { tProp.value = texts; tProp.disabled = false; }
      function attach(sel, file, cb) {
        var input = form.querySelector(sel);
        if (input && file) {
          var dt = new DataTransfer();
          dt.items.add(file);
          input.files = dt.files;
          input.disabled = false;
        }
        cb();
      }
      tex.toBlob(function (b) {
        attach("[data-dz-layout-file]", b ? new File([b], "print-layout.png", { type: "image/png" }) : null, function () {
          if (mug) {
            mug.snapshot(function (f) {
              attach("[data-dz-preview-file]", f, function () { form.submit(); });
            });
          } else form.submit();
        });
      }, "image/png", 0.95);
    });
  }
})();
